import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Star } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { ImageUploader, GalleryUploader } from "@/components/admin/ImageUploader";

const input = "w-full bg-background border border-border px-3 py-2 text-sm outline-none focus:border-foreground transition-colors";
const labelCls = "text-[11px] tracking-[0.12em] uppercase text-muted-foreground block mb-1.5";

const EMPTY = { name: "", location: "", type: "", year: "", short_description: "", overview: "", scope_of_work: [], systems_materials: [], completion_date: "", hero_image: "", gallery: [], before_image: "", after_image: "", process_video: "", featured: false, order: 0 };

const StrList = ({ items, onChange, ph }) => (
  <div className="space-y-2">
    {items.map((v, i) => (
      <div key={i} className="flex gap-2">
        <input className={input} value={v} placeholder={ph} onChange={(e) => onChange(items.map((x, idx) => idx === i ? e.target.value : x))} />
        <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-destructive px-2"><Trash2 size={15} /></button>
      </div>
    ))}
    <button onClick={() => onChange([...items, ""])} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Plus size={14} /> Add</button>
  </div>
);

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/projects").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setF(EMPTY); setEditing("new"); };
  const openEdit = (p) => { setF({ ...EMPTY, ...p }); setEditing(p.id); };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...f, order: Number(f.order) || 0 };
      delete payload.id; delete payload.slug; delete payload.created_at;
      if (editing === "new") await api.post("/projects", payload);
      else await api.put(`/projects/${editing}`, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this project?")) return; await api.delete(`/projects/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-display font-extrabold text-3xl tracking-tight">Projects</h1><p className="text-muted-foreground text-sm mt-1">{items.length} projects</p></div>
        <button onClick={openNew} data-testid="new-project-btn" className="flex items-center gap-2 bg-foreground text-background px-5 py-2.5 text-[12px] tracking-[0.1em] uppercase hover:bg-onyx-wood hover:text-onyx-black transition-colors"><Plus size={15} /> New Project</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="border border-border bg-card group" data-testid={`project-item-${p.id}`}>
            <div className="relative h-40 overflow-hidden bg-secondary">
              {p.hero_image && <img src={fileUrl(p.hero_image)} alt="" className="w-full h-full object-cover" />}
              {p.featured && <span className="absolute top-2 left-2 bg-onyx-wood text-onyx-black text-[10px] uppercase tracking-wide px-2 py-1 flex items-center gap-1"><Star size={10} /> Featured</span>}
            </div>
            <div className="p-4">
              <h3 className="font-display font-bold tracking-tight">{p.name}</h3>
              <p className="text-xs text-muted-foreground mt-1">{p.location} · {p.type} · {p.year}</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => openEdit(p)} data-testid={`edit-project-${p.id}`} className="flex-1 border border-border py-2 text-xs uppercase tracking-wide hover:bg-secondary transition-colors flex items-center justify-center gap-1.5"><Pencil size={13} /> Edit</button>
                <button onClick={() => remove(p.id)} className="border border-border py-2 px-3 text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={() => setEditing(null)}>
          <div className="w-full max-w-2xl bg-background h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="project-editor">
            <div className="sticky top-0 bg-card border-b border-border px-6 h-16 flex items-center justify-between z-10">
              <h2 className="font-display font-bold text-lg">{editing === "new" ? "New Project" : "Edit Project"}</h2>
              <button onClick={() => setEditing(null)}><X /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Name</label><input data-testid="project-name" className={input} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
                <div><label className={labelCls}>Location</label><input className={input} value={f.location} onChange={(e) => set("location", e.target.value)} /></div>
                <div><label className={labelCls}>Type</label><input className={input} value={f.type} onChange={(e) => set("type", e.target.value)} /></div>
                <div><label className={labelCls}>Year</label><input className={input} value={f.year} onChange={(e) => set("year", e.target.value)} /></div>
                <div><label className={labelCls}>Completion Date</label><input className={input} value={f.completion_date} onChange={(e) => set("completion_date", e.target.value)} /></div>
                <div><label className={labelCls}>Order</label><input type="number" className={input} value={f.order} onChange={(e) => set("order", e.target.value)} /></div>
              </div>
              <div><label className={labelCls}>Short Description</label><input className={input} value={f.short_description} onChange={(e) => set("short_description", e.target.value)} /></div>
              <div><label className={labelCls}>Overview</label><textarea rows={4} className={`${input} resize-none`} value={f.overview} onChange={(e) => set("overview", e.target.value)} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured on homepage</label>
              <ImageUploader value={f.hero_image} onChange={(v) => set("hero_image", v)} label="Hero Image" testid="project-hero-upload" />
              <div className="grid grid-cols-2 gap-4">
                <ImageUploader value={f.before_image} onChange={(v) => set("before_image", v)} label="Before" height="h-28" />
                <ImageUploader value={f.after_image} onChange={(v) => set("after_image", v)} label="After" height="h-28" />
              </div>
              <GalleryUploader value={f.gallery} onChange={(v) => set("gallery", v)} label="Gallery" />
              <div><label className={labelCls}>Scope of Work</label><StrList items={f.scope_of_work} onChange={(v) => set("scope_of_work", v)} ph="Scope item" /></div>
              <div><label className={labelCls}>Systems & Materials</label><StrList items={f.systems_materials} onChange={(v) => set("systems_materials", v)} ph="Material" /></div>
              <button onClick={save} disabled={saving} data-testid="save-project" className="w-full bg-foreground text-background py-3 text-[12px] tracking-[0.1em] uppercase hover:bg-onyx-wood hover:text-onyx-black transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save Project"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
